import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';
import {
  getAccount,
  inviteUserToAccount,
  deleteUser,
  updateUser,
} from '../../services/accountService/accountService';
import {
  getAccountRequest,
  getAccountSuccess,
  getAccountFailure,
  inviteUserToAccountSuccess,
  inviteUserToAccountFailure,
  deleteUserSuccess,
  deleteUserFailure,
  updateUserSuccess,
  updateUserFailure,
} from './accounts.action';
import {
  GET_ACCOUNT_REQUEST,
  INVITE_USER_TO_ACCOUNT_REQUEST,
  DELETE_USER_REQUEST,
  UPDATE_USER_REQUEST,
} from './accounts.types';
import { GetAccountResponse } from 'types/accounts/accounts.types';

interface InviteUserToAccountAction {
  type: typeof INVITE_USER_TO_ACCOUNT_REQUEST;
  payload: {
    email: string;
    firstName: string;
    lastName: string;
    role: 'admin' | 'member';
    permissions: {
      type: 'all' | 'custom';
      workspaces?: { [workspaceId: string]: string };
    };
  };
}

interface DeleteUserAction {
  type: typeof DELETE_USER_REQUEST;
  payload: string; // userId
}

interface UpdateUserAction {
  type: typeof UPDATE_USER_REQUEST;
  payload: {
    userId: string;
    data: {
      firstName: string;
      lastName: string;
      email: string;
      role: 'admin' | 'member';
      permissions: {
        type: 'all' | 'custom';
        workspaceRole?: 'admin' | 'member';
        workspaces?: { [workspaceId: string]: string };
      };
    };
  };
}

// Get Account Saga
function* getAccountSaga(): SagaIterator {
  try {
    const response: GetAccountResponse = yield call(getAccount);
    yield put(getAccountSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch account';
    yield put(getAccountFailure(errorMessage));
  }
}

// Invite User to Account Saga
function* inviteUserToAccountSaga(
  action: InviteUserToAccountAction
): SagaIterator {
  try {
    const response: { message: string } = yield call(
      inviteUserToAccount,
      action.payload
    );

    // Handle response message
    const message = response.message || 'Invitation sent successfully';

    yield put(inviteUserToAccountSuccess(message));
    yield put(getAccountRequest()); // Refresh account data
  } catch (error: any) {
    console.error('Error inviting user to account:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to send invitation';
    yield put(inviteUserToAccountFailure(errorMessage));
  }
}

// Delete User Saga
function* deleteUserSaga(action: DeleteUserAction): SagaIterator {
  try {
    const response: { message: string } = yield call(
      deleteUser,
      action.payload
    );
    const accountResponse: GetAccountResponse = yield call(getAccount);

    const message = response.message || 'User deleted successfully';
    yield put(deleteUserSuccess(message));
    yield put(getAccountSuccess(accountResponse)); // Refresh invited users list
  } catch (error: any) {
    console.error('Error deleting user:', error);
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to delete user';
    yield put(deleteUserFailure(errorMessage));
  }
}

// Update User Saga
function* updateUserSaga(action: UpdateUserAction): SagaIterator {
  try {
    const response: { message: string } = yield call(
      updateUser,
      action.payload.userId,
      action.payload.data
    );

    const message = response.message || 'User updated successfully';
    yield put(updateUserSuccess(message));
    yield put(getAccountRequest()); // Refresh account data
  } catch (error: any) {
    console.error('Error updating user:', error);
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to update user';
    yield put(updateUserFailure(errorMessage));
  }
}

// Root accounts saga
export function* accountsSaga() {
  yield takeEvery(GET_ACCOUNT_REQUEST, getAccountSaga);
  yield takeEvery(INVITE_USER_TO_ACCOUNT_REQUEST, inviteUserToAccountSaga);
  yield takeEvery(DELETE_USER_REQUEST, deleteUserSaga);
  yield takeEvery(UPDATE_USER_REQUEST, updateUserSaga);
}
